import React from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';

// Layout components
import Layout from '../../components/layout/Layout';
import Section from '../../components/layout/Section';
import Container from '../../components/layout/Container';

// Components
import ContactForm from '../../components/forms/ContactForm';

// Data
import { contactInfo } from '../../data/mockData';

/**
 * Contact Page component
 */
const ContactPage: React.FC = () => {
  // Get any query parameters (for pre-selected service)
  const [searchParams] = useSearchParams();
  const inquiryType = searchParams.get('inquiry') || '';
  const service = searchParams.get('service') || '';
  
  return (
    <Layout>
      {/* Hero section */}
      <div className="relative overflow-hidden bg-neu-bg pt-28 pb-14">
        <div
          className="pointer-events-none absolute top-0 right-0 h-full flex items-center opacity-50 overflow-hidden"
          style={{ width: '38%' }}
        >
          <div
            className="relative flex items-center justify-center"
            style={{ width: 380, height: 380, marginLeft: 'auto', marginRight: '-80px' }}
          >
            <div className="absolute rounded-full" style={{ width: 380, height: 380, boxShadow: '10px 10px 22px rgb(163,177,198,0.5), -10px -10px 22px rgba(255,255,255,0.4)', background: '#E0E5EC' }} />
            <div className="absolute rounded-full" style={{ width: 260, height: 260, boxShadow: 'inset 7px 7px 14px rgb(163,177,198,0.6), inset -7px -7px 14px rgba(255,255,255,0.5)', background: '#E0E5EC' }} />
            <div className="absolute rounded-full" style={{ width: 140, height: 140, boxShadow: '7px 7px 14px rgb(163,177,198,0.6), -7px -7px 14px rgba(255,255,255,0.5)', background: '#E0E5EC' }} />
            <div className="absolute rounded-full animate-float" style={{ width: 56, height: 56, background: 'linear-gradient(135deg, #6C63FF 0%, #8B84FF 100%)', boxShadow: '0 4px 16px rgba(108,99,255,0.45)' }} />
          </div>
        </div>
        <Container>
          <motion.div
            className="relative z-10 max-w-4xl mx-auto text-center"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <h1 className="font-display text-4xl md:text-5xl font-extrabold tracking-tight text-neu-text mb-6 leading-tight">
              Get in Touch
            </h1>
            <p className="text-lg md:text-xl text-neu-muted mb-8 max-w-2xl mx-auto leading-relaxed">
              Have questions about our AI solutions? Ready to start your AI journey?
              Our team of experts is here to help.
            </p>
          </motion.div>
        </Container>
      </div>

      {/* Contact form and info section */}
      <Section bgColor="bg-neu-bg" padding="md">
        <Container>
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
            {/* Contact form - takes up 3/5 of the grid on large screens */}
            <motion.div
              className="lg:col-span-3"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <div className="bg-neu-bg rounded-neu shadow-neu-extruded hover:shadow-neu-extruded-hover transition-all duration-300 p-6 md:p-8">
                <h2 className="text-2xl font-bold mb-2 text-neu-text">Request a Consultation</h2>
                <p className="text-neu-muted mb-6">
                  Fill out the form below and one of our AI specialists will get back to you promptly.
                </p>

                <ContactForm
                  defaultService={service}
                  defaultInquiryType={inquiryType || 'consultation'}
                />
              </div>
            </motion.div>

            {/* Contact information - takes up 2/5 of the grid on large screens */}
            <motion.div
              className="lg:col-span-2"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <div className="bg-neu-bg rounded-neu shadow-neu-extruded p-6 md:p-8 h-full">
                <h2 className="text-2xl font-bold mb-6 text-neu-text">Contact Information</h2>

                <div className="space-y-6">
                  {/* Email */}
                  <div className="flex items-start">
                    <div className="flex-shrink-0 w-10 h-10 bg-neu-bg rounded-full flex items-center justify-center text-neu-accent shadow-neu-extruded-sm">
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <div className="ml-4">
                      <h3 className="text-lg font-semibold text-neu-text">Email</h3>
                      <p className="text-neu-muted">
                        <a href={`mailto:${contactInfo.email}`} className="hover:text-neu-accent-light transition-colors">
                          {contactInfo.email}
                        </a>
                      </p>
                    </div>
                  </div>

                  {/* Phone */}
                  <div className="flex items-start">
                    <div className="flex-shrink-0 w-10 h-10 bg-neu-bg rounded-full flex items-center justify-center text-neu-accent shadow-neu-extruded-sm">
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                      </svg>
                    </div>
                    <div className="ml-4">
                      <h3 className="text-lg font-semibold text-neu-text">Phone</h3>
                      <p className="text-neu-muted">
                        <a href={`tel:${contactInfo.phone.replace(/\D/g, '')}`} className="hover:text-neu-accent-light transition-colors">
                          {contactInfo.phone}
                        </a>
                      </p>
                    </div>
                  </div>

                  {/* Address */}
                  <div className="flex items-start">
                    <div className="flex-shrink-0 w-10 h-10 bg-neu-bg rounded-full flex items-center justify-center text-neu-accent shadow-neu-extruded-sm">
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                    </div>
                    <div className="ml-4">
                      <h3 className="text-lg font-semibold text-neu-text">Address</h3>
                      <p className="text-neu-muted">
                        {contactInfo.address}
                      </p>
                    </div>
                  </div>

                  {/* Hours */}
                  <div className="flex items-start">
                    <div className="flex-shrink-0 w-10 h-10 bg-neu-bg rounded-full flex items-center justify-center text-neu-accent shadow-neu-extruded-sm">
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <div className="ml-4">
                      <h3 className="text-lg font-semibold text-neu-text">Business Hours</h3>
                      <p className="text-neu-muted">
                        {contactInfo.hours}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Social Media Links */}
                <div className="mt-8">
                  <h3 className="text-lg font-semibold mb-4 text-neu-text">Connect With Us</h3>
                  <div className="flex space-x-4">
                    <a
                      href={contactInfo.socialMedia.linkedin}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-10 h-10 bg-neu-bg shadow-neu-extruded-sm rounded-full flex items-center justify-center text-neu-accent hover:shadow-neu-extruded hover:text-neu-accent-light transition-all duration-300"
                      aria-label="LinkedIn"
                    >
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z" />
                      </svg>
                    </a>
                    <a
                      href={contactInfo.socialMedia.twitter}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-10 h-10 bg-neu-bg shadow-neu-extruded-sm rounded-full flex items-center justify-center text-neu-accent hover:shadow-neu-extruded hover:text-neu-accent-light transition-all duration-300"
                      aria-label="Twitter"
                    >
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M24 4.557c-.883.392-1.832.656-2.828.775 1.017-.609 1.798-1.574 2.165-2.724-.951.564-2.005.974-3.127 1.195-.897-.957-2.178-1.555-3.594-1.555-3.179 0-5.515 2.966-4.797 6.045-4.091-.205-7.719-2.165-10.148-5.144-1.29 2.213-.669 5.108 1.523 6.574-.806-.026-1.566-.247-2.229-.616-.054 2.281 1.581 4.415 3.949 4.89-.693.188-1.452.232-2.224.084.626 1.956 2.444 3.379 4.6 3.419-2.07 1.623-4.678 2.348-7.29 2.04 2.179 1.397 4.768 2.212 7.548 2.212 9.142 0 14.307-7.721 13.995-14.646.962-.695 1.797-1.562 2.457-2.549z" />
                      </svg>
                    </a>
                    <a
                      href={contactInfo.socialMedia.facebook}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-10 h-10 bg-neu-bg shadow-neu-extruded-sm rounded-full flex items-center justify-center text-neu-accent hover:shadow-neu-extruded hover:text-neu-accent-light transition-all duration-300"
                      aria-label="Facebook"
                    >
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M9 8h-3v4h3v12h5v-12h3.642l.358-4h-4v-1.667c0-.955.192-1.333 1.115-1.333h2.885v-5h-3.808c-3.596 0-5.192 1.583-5.192 4.615v3.385z" />
                      </svg>
                    </a>
                    <a
                      href={contactInfo.socialMedia.youtube}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-10 h-10 bg-neu-bg shadow-neu-extruded-sm rounded-full flex items-center justify-center text-neu-accent hover:shadow-neu-extruded hover:text-neu-accent-light transition-all duration-300"
                      aria-label="YouTube"
                    >
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M19.615 3.184c-3.604-.246-11.631-.245-15.23 0-3.897.266-4.356 2.62-4.385 8.816.029 6.185.484 8.549 4.385 8.816 3.6.245 11.626.246 15.23 0 3.897-.266 4.356-2.62 4.385-8.816-.029-6.185-.484-8.549-4.385-8.816zm-10.615 12.816v-8l8 3.993-8 4.007z" />
                      </svg>
                    </a>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </Container>
      </Section>
      
      {/* FAQ Section */}
      <Section bgColor="bg-neu-bg" padding="md">
        <Container>
          <div className="text-center mb-10">
            <h2 className="text-2xl md:text-3xl font-bold mb-4 text-neu-text">Common Questions</h2>
            <p className="text-lg text-neu-muted max-w-2xl mx-auto">
              Here are some quick answers to questions we frequently receive.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-5xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <div className="bg-neu-bg rounded-neu p-6 shadow-neu-extruded hover:shadow-neu-extruded-hover transition-all duration-300">
                <h3 className="text-xl font-semibold mb-3 text-neu-text">How quickly will you respond to my inquiry?</h3>
                <p className="text-neu-muted">
                  We aim to respond to all inquiries within 1 business day. For urgent matters, please call our office directly.
                </p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              <div className="bg-neu-bg rounded-neu p-6 shadow-neu-extruded hover:shadow-neu-extruded-hover transition-all duration-300">
                <h3 className="text-xl font-semibold mb-3 text-neu-text">What should I prepare for my consultation?</h3>
                <p className="text-neu-muted">
                  It's helpful to have a basic understanding of your current challenges, business goals, and any specific AI applications you're interested in. Don't worry if you're not sure - we're here to guide you!
                </p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <div className="bg-neu-bg rounded-neu p-6 shadow-neu-extruded hover:shadow-neu-extruded-hover transition-all duration-300">
                <h3 className="text-xl font-semibold mb-3 text-neu-text">Do you offer remote consultations?</h3>
                <p className="text-neu-muted">
                  Yes, we offer both in-person and virtual consultations. Our team can meet via video conference at a time that works for you, regardless of your location.
                </p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.3 }}
            >
              <div className="bg-neu-bg rounded-neu p-6 shadow-neu-extruded hover:shadow-neu-extruded-hover transition-all duration-300">
                <h3 className="text-xl font-semibold mb-3 text-neu-text">What information do I need to provide for a quote?</h3>
                <p className="text-neu-muted">
                  For an accurate quote, we'll need details about your project scope, business size, existing systems, and timeline. During your consultation, we'll guide you through the information we need.
                </p>
              </div>
            </motion.div>
          </div>
        </Container>
      </Section>
    </Layout>
  );
};

export default ContactPage;